import { useMemo, useState } from "react";
import { Button, Card, Message, Space, Table, Tag, Typography } from "@arco-design/web-react";
import type { TableColumnProps } from "@arco-design/web-react";
import { ChevronDown, ChevronUp, Plus } from "lucide-react";

const { Text } = Typography;

/** 范围类型：单人调整与整团/班级/角色变化。 */
export type BusinessChangeScopeType = "person" | "class" | "group" | "role";

/** 业务变化状态。 */
export type BusinessChangeStatus =
  | "pending_ops"
  | "pending_sales_receivable"
  | "effective";

export type BusinessChangeRecord = {
  id: string;
  scopeType: BusinessChangeScopeType;
  title: string;
  description: string;
  impactText: string;
  impactCountText: string;
  pendingReceivableText: string;
  pendingReceivableTone?: "muted" | "amount" | "neutral";
  confirmedCostText: string;
  confirmedCostTone?: "muted" | "amount" | "deduct";
  status: BusinessChangeStatus;
};

type Props = {
  /** 由调用方传入已落库的业务变化记录。 */
  records?: BusinessChangeRecord[];
};

const SCOPE_LABEL: Record<BusinessChangeScopeType, string> = {
  person: "单人",
  class: "班级",
  group: "整团",
  role: "角色",
};

const SCOPE_COLOR: Record<BusinessChangeScopeType, string> = {
  person: "green",
  class: "purple",
  group: "arcoblue",
  role: "magenta",
};

const STATUS_LABEL: Record<BusinessChangeStatus, string> = {
  pending_ops: "待计调确认",
  pending_sales_receivable: "待销售确认应收",
  effective: "已生效",
};

const STATUS_COLOR: Record<BusinessChangeStatus, string> = {
  pending_ops: "orangered",
  pending_sales_receivable: "orangered",
  effective: "green",
};

/** 根据业务变化状态生成操作文案。 */
function buildActionLabel(status: BusinessChangeStatus): string {
  if (status === "pending_ops") {
    return "查看计调进展";
  }
  if (status === "pending_sales_receivable") {
    return "确认应收";
  }
  return "查看结果";
}

/** 按金额语义渲染对应颜色文本。 */
function renderToneText(
  text: string,
  tone: BusinessChangeRecord["pendingReceivableTone"] | BusinessChangeRecord["confirmedCostTone"],
) {
  const className =
    tone === "amount"
      ? "order-handover-business-change-amount"
      : tone === "deduct"
        ? "order-handover-business-change-deduct"
        : tone === "muted"
          ? "order-handover-business-change-muted"
          : "order-handover-business-change-neutral";
  return <span className={className}>{text}</span>;
}

/** 出行名单下方的业务变化记录卡片：默认折叠，展示调整记录与分页。 */
export function OrderHandoverBusinessChangeCard({ records = [] }: Props) {
  const [collapsed, setCollapsed] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const summary = useMemo(() => {
    const total = records.length;
    const personRecords = records.filter((item) => item.scopeType === "person");
    const groupRecords = records.filter((item) => item.scopeType !== "person");
    const pending = records.filter((item) => item.status !== "effective").length;
    const personCount = personRecords.reduce(
      (sum, item) => sum + (Number.parseInt(item.impactCountText, 10) || 0),
      0,
    );
    const groupPersonTimes = groupRecords.reduce(
      (sum, item) => sum + (Number.parseInt(item.impactCountText, 10) || 0),
      0,
    );
    return {
      total,
      personCount: personRecords.length,
      personPeople: personCount,
      groupCount: groupRecords.length,
      groupPersonTimes,
      pending,
    };
  }, [records]);

  const columns: TableColumnProps<BusinessChangeRecord>[] = useMemo(
    () => [
      {
        title: "业务变化单",
        dataIndex: "id",
        width: 140,
        render: (value: string) => <Text className="order-handover-business-change-id">{value}</Text>,
      },
      {
        title: "范围类型",
        dataIndex: "scopeType",
        width: 96,
        render: (value: BusinessChangeScopeType) => (
          <Tag color={SCOPE_COLOR[value]} size="small">
            {SCOPE_LABEL[value]}
          </Tag>
        ),
      },
      {
        title: "变化内容",
        dataIndex: "title",
        width: 220,
        render: (_: string, row: BusinessChangeRecord) => (
          <div className="order-handover-business-change-content">
            <strong>{row.title}</strong>
            <span>{row.description}</span>
          </div>
        ),
      },
      {
        title: "影响范围",
        dataIndex: "impactText",
        width: 160,
        render: (_: string, row: BusinessChangeRecord) => (
          <span>
            {row.impactText} · {row.impactCountText}
          </span>
        ),
      },
      {
        title: "待确认应收",
        dataIndex: "pendingReceivableText",
        width: 140,
        render: (_: string, row: BusinessChangeRecord) =>
          renderToneText(row.pendingReceivableText, row.pendingReceivableTone),
      },
      {
        title: "确认成本",
        dataIndex: "confirmedCostText",
        width: 140,
        render: (_: string, row: BusinessChangeRecord) =>
          renderToneText(row.confirmedCostText, row.confirmedCostTone),
      },
      {
        title: "状态",
        dataIndex: "status",
        width: 140,
        render: (value: BusinessChangeStatus) => (
          <Tag color={STATUS_COLOR[value]} size="small">
            {STATUS_LABEL[value]}
          </Tag>
        ),
      },
      {
        title: "操作",
        key: "action",
        dataIndex: "status",
        width: 120,
        fixed: "right",
        render: (value: BusinessChangeStatus) => (
          <Button
            type="text"
            className="order-handover-business-change-action"
            onClick={() => Message.info("业务变化详情功能开发中")}
          >
            {buildActionLabel(value)}
          </Button>
        ),
      },
    ],
    [],
  );

  return (
    <Card
      className="order-handover-business-change-card"
      bordered
      data-testid="order-handover-business-change-card"
      title={
        <div className="order-handover-business-change-title-wrap">
          <span>业务变化记录</span>
          {collapsed ? null : (
            <Text className="order-handover-muted order-handover-business-change-hint">
              单人调整在名单中直接操作；退团、插班、角色变化进入总体业务变化页。
            </Text>
          )}
        </div>
      }
      extra={
        <Space size={8}>
          <Button
            type="text"
            icon={collapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
            aria-label={collapsed ? "展开业务变化记录" : "折叠业务变化记录"}
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? "展开" : "折叠"}
          </Button>
          {collapsed ? null : (
            <Button
              type="primary"
              icon={<Plus size={16} />}
              onClick={() => Message.info("退团 / 分类变化功能开发中")}
            >
              发起退团 / 分类变化
            </Button>
          )}
        </Space>
      }
    >
      {collapsed ? null : (
        <>
          <div
            className="order-handover-business-change-summary"
            data-testid="order-handover-business-change-summary"
          >
            <div>
              <span>业务变化记录</span>
              <strong>{`${summary.total} 笔`}</strong>
            </div>
            <div>
              <span>单人范围变化</span>
              <strong>{`${summary.personCount} 笔 · ${summary.personPeople} 人`}</strong>
            </div>
            <div>
              <span>班级 / 整团 / 角色变化</span>
              <strong>{`${summary.groupCount} 笔 · 影响 ${summary.groupPersonTimes} 人次`}</strong>
            </div>
            <div className="is-pending">
              <span>待处理</span>
              <strong>{`${summary.pending} 笔`}</strong>
            </div>
          </div>
          <Table
            rowKey="id"
            columns={columns}
            data={records}
            pagination={{
              current: page,
              pageSize,
              total: records.length,
              showTotal: true,
              sizeCanChange: true,
              pageSizeChangeResetCurrent: true,
              onChange: (current, size) => {
                setPage(current);
                setPageSize(size);
              },
            }}
            scroll={{ x: 1100 }}
            noDataElement="暂无业务变化记录"
          />
        </>
      )}
    </Card>
  );
}
